import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, ReplaySubject, of, interval, Subscription } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { JwtHelperService } from '@auth0/angular-jwt';

import { GlobalErrorHandlerService } from './global-error-handler.service';

import { Healthgroup } from '../healthgroup/healthgroup';
import { Clinic } from '../clinic/clinic';
import { HealthgroupService } from './healthgroup.service';
import { UserPreferenceService } from './user-preference.service';

const httpOptions = {
  headers: new HttpHeaders({'Content-Type': 'application/json'})
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  static readonly ADMIN_ROLE: string = 'admin';
  static readonly DOCTOR_ROLE: string = 'doc';
  static readonly RECEPTIONIST_ROLE: string = 'recep';
  static readonly JWT_TOKEN_KEY: string = 'id_token';
  static readonly JWT_TOKEN_EXPIRES_IN_KEY: string = 'expires_in';
  static readonly SESSION_TIMEOUT: number = 1800000;
  static readonly SESSION_TIMEOUT_INTERVAL: number = 900000;

  userSignedIn$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  healthGroupLoaded$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  clinicLoaded$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  healthGroup: Healthgroup = new Healthgroup();
  currentUser: any;
  private selectedClinic: Clinic;
  private jwtHelper: JwtHelperService;
  private sessionNotificationSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private notifService: ToastrService,
    private hgroupService: HealthgroupService,
    private userPreferenceService: UserPreferenceService,
    private errorHandler: GlobalErrorHandlerService) {

    this.jwtHelper = new JwtHelperService();
    this.errorHandler.clearLocalAuthData = this.clearLocalAuthData.bind(this);

    if (this.isAuthenticated()) {
      this.refreshCurrentSession();

    } else {
      this.clearSession();
    }
  }

  signOut(): Observable<any> {
    return this.http
        .delete('/api/sign_out')
        .pipe(map(
            res => { this.clearLocalAuthData(); }
          ), catchError(error => this.errorHandler.handleError(error))
        );
  }

  clearLocalAuthData(): void {
    this.sessionNotificationSubscription.unsubscribe();
    this.clearSession();
    this.healthGroup = new Healthgroup();
    this.selectedClinic = null;
    this.currentUser = null;
    this.userSignedIn$.next(false);
    this.healthGroupLoaded$.next(false);
    this.clinicLoaded$.next(false);
  }

  signIn(signInData: {login: string, password: string}, selectedClinic: Clinic): Observable<any> {
    return this.http
        .post('/api/sign_in', {'user': {'email': signInData.login, 'password': signInData.password}}, { observe: 'response' })
        .pipe(map(
            res => {
              this.setSession(res);
              this.currentUser = res.body;
              this.loadHealthGroup(this.currentUser.health_group_id);
              this.saveSelectedClinic(this.currentUser.id, selectedClinic.id);
              this.userSignedIn$.next(true);
              this.sessionNotificationSubscription = interval(AuthService.SESSION_TIMEOUT_INTERVAL)
                      .subscribe(timeoutValue => this.verifySessionExpiration());
              return res;
            }
          ), catchError(error => this.errorHandler.handleError(error))
        );
  }

  changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Observable<any> {
    return this.http.put('/api/user_preferences/password',
      {
        id: this.currentUser.id,
        password: newPassword,
        password_confirmation: confirmPassword,
        password_current: currentPassword
      }
    ).pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  isAuthenticated(): boolean {
    const now: Date = new Date();
    const expiresIn: Date = this.getSessionExpiration();

    return now < expiresIn;
  }

  existsSessionToken(): boolean {
    const currentToken: string = this.getCurrentToken();

    return currentToken !== null;
  }

  isAdmin():boolean {
    return this.checkRole(AuthService.ADMIN_ROLE);
  }

  isRecepcionist():boolean {
    return this.checkRole(AuthService.RECEPTIONIST_ROLE);
  }

  isDoctor():boolean {
    return this.checkRole(AuthService.DOCTOR_ROLE);
  }

  checkRole(role:string):boolean {
    if (!this.isAuthenticated()) {
      return false;
    }
    let userRole = this.currentUser['roles'][0]['name'];
    return userRole == role;
  }

  getSelectedClinic(): Clinic {
    if (!this.selectedClinic) {
      this.userPreferenceService.getSelectedClinic(this.currentUser.id)
        .pipe(catchError(error => this.errorHandler.handleError(error)))
        .subscribe(clinic => {
          this.selectedClinic = clinic;
          this.clinicLoaded$.next(true);
        });
    }

    return this.selectedClinic;
  }

  useHeaders(): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }
    return this.currentUser.use_headers;
  }

  private refreshCurrentSession(): void {
    const encriptedToken = this.getCurrentToken();
    const decriptedToken = this.jwtHelper.decodeToken(encriptedToken);
    const id = decriptedToken.sub;
    this.hgroupService.getCurrentLoggedInUser(id).subscribe(res => {
        this.currentUser = res;
        this.loadHealthGroup(this.currentUser.health_group_id);
        this.loadSelectedClinic(this.currentUser.id);
        this.userSignedIn$.next(true);
        this.sessionNotificationSubscription = interval(AuthService.SESSION_TIMEOUT_INTERVAL)
                .subscribe(timeoutValue => this.verifySessionExpiration());
    });
  }

  private saveSelectedClinic(userId: number, clinicId: number): void {
    this.userPreferenceService.updateSelectedClinic(userId, clinicId)
      .pipe(catchError(error => this.errorHandler.handleError(error)))
      .subscribe(selectedClinic => {
        this.selectedClinic = selectedClinic;
        this.clinicLoaded$.next(true);
      });
  }

  private loadSelectedClinic(userId: number): void {
    this.userPreferenceService.getSelectedClinic(userId)
      .pipe(catchError(error => this.errorHandler.handleError(error)))
      .subscribe(selectedClinic => {
        this.selectedClinic = selectedClinic;
        this.clinicLoaded$.next(true);
      });
  }

  private loadHealthGroup(healthGroupId: number): void {
    this.hgroupService.getHealthgroup(healthGroupId)
      .pipe(catchError(error => this.errorHandler.handleError(error)))
      .subscribe(healthGroup => {
        this.healthGroup = healthGroup;
        this.healthGroupLoaded$.next(true);
      }
    );
  }

  private setSession(authResult): void {
    const token = authResult.headers.get('authorization').replace('Bearer ', '');
    const expiresIn = this.jwtHelper.getTokenExpirationDate(token);

    localStorage.setItem(AuthService.JWT_TOKEN_KEY, token);
    localStorage.setItem(AuthService.JWT_TOKEN_EXPIRES_IN_KEY, JSON.stringify(expiresIn));
  }

  private verifySessionExpiration(): void {
    const now: number = new Date().getTime();
    const expiresIn: number = this.getSessionExpiration().getTime();
    const diff = expiresIn - now;

    if (diff < AuthService.SESSION_TIMEOUT) {
      this.notifService.warning(`Sua sessão irá expirar em ${Math.ceil(diff/60000)} minutos`,
        '', { timeOut: diff, disableTimeOut: 'extendedTimeOut' });
    }
  }

  private getSessionExpiration(): Date {
    const expiresInString = localStorage.getItem(AuthService.JWT_TOKEN_EXPIRES_IN_KEY);
    return new Date(JSON.parse(expiresInString));
  }

  private getCurrentToken(): string {
    return localStorage.getItem(AuthService.JWT_TOKEN_KEY);
  }

  private clearSession(): void {
    localStorage.removeItem(AuthService.JWT_TOKEN_KEY);
    localStorage.removeItem(AuthService.JWT_TOKEN_EXPIRES_IN_KEY);
  }
}

